import { ethers } from "ethers";

/** A token amount for display with at most `places` decimals. Fees round up
 * and amounts someone receives round down, so a shown fee and a shown net
 * never add up to more than was entered. */
export function formatTokenAmount(
  value: bigint,
  decimals: number,
  round: "down" | "up",
  places = 6,
): string {
  if (value < 0n)
    return `-${formatTokenAmount(-value, decimals, round === "down" ? "up" : "down", places)}`;
  const shown = Math.min(places, decimals);
  const unit = 10n ** BigInt(decimals - shown);
  let scaled = value / unit;
  if (round === "up" && value % unit !== 0n) scaled += 1n;
  return ethers.formatUnits(scaled, shown);
}

/** Display only: never use this string for MAX, quotes, storage or submission.
 * Decimal strings avoid floating-point loss. Tiny balances remain visible. */
export function formatDisplayAmount(
  amount: string,
  round: "down" | "up" = "down",
): string {
  const match = /^(-?)(\d+)\.(\d+)$/.exec(amount);
  if (!match || match[3].length <= 6) return amount;
  const value = BigInt(`${match[1]}${match[2]}${match[3]}`);
  const formatted = formatTokenAmount(value, match[3].length, round);
  if (value !== 0n && /^-?0\.0$/.test(formatted))
    return value < 0n ? ">-0.000001" : "<0.000001";
  return formatted;
}

export function formatDisplayUnits(
  value: bigint | string,
  decimals: number,
  round: "down" | "up" = "down",
): string {
  return formatDisplayAmount(ethers.formatUnits(value, decimals), round);
}
